import {
  Box,
  Flex,
  Icon,
  Link,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react"
import CarouselButton from "@components/buttons/CarouselButton"
import { EmenuSamples } from "@definitions/types"
import SvgPhone from "@public/svg/misc/phone.svg"
import { partitionArray } from "@utils/utils"
import Image from "next/image"
import NextLink from "next/link"
import { Carousel } from "react-responsive-carousel"

import styles from "./QRSamples.module.css"

export default function QRSamples(props: { samplesList: EmenuSamples[] }) {
  const { samplesList } = props

  const frameSize = useBreakpointValue({ base: 180, sm: 224 }) || 224
  const imageSize = useBreakpointValue({ base: 160, sm: 200 }) || 200
  const arraySize = useBreakpointValue({ base: 1, md: 3, xl: 4 })
  const centerMode = useBreakpointValue({ base: true, sm: false })
  const samples = partitionArray(samplesList, arraySize ?? 4)

  return (
    <Carousel
      ariaLabel={"eMenu Clients Carousel"}
      autoPlay={false}
      emulateTouch={true}
      showArrows={true}
      showStatus={false}
      showIndicators={false}
      showThumbs={false}
      preventMovementUntilSwipeScrollTolerance={true}
      infiniteLoop={!centerMode}
      centerMode={centerMode}
      className={styles.carousel}
      renderArrowPrev={(onClickHandler, hasPrev, label) => (
        <CarouselButton
          onClick={onClickHandler}
          aria-label={label}
          direction={"prev"}
          transform={"translateY(-110px)"}
        />
      )}
      renderArrowNext={(onClickHandler, hasNext, label) => (
        <CarouselButton
          onClick={onClickHandler}
          aria-label={label}
          direction={"next"}
          transform={"translateY(-110px)"}
        />
      )}
    >
      {samples.map((sampleSlide, index) => (
        <Flex
          key={index}
          w={{ base: "80%", sm: "full" }}
          wrap={"wrap"}
          justifyContent={{ base: "space-evenly" }}
        >
          {sampleSlide?.map(
            ({ id, name, url, color, image, hover_image, qr_code_svg }) => (
              <Link
                as={NextLink}
                href={url || "/"}
                key={id}
                role={"group"}
                target={"_blank"}
                aria-label={name || "eMenu"}
                title={name || "eMenu"}
              >
                <VStack spacing={{ base: 8, sm: 12 }}>
                  <VStack
                    alignItems={"center"}
                    position={"relative"}
                  >
                    <Icon
                      as={SvgPhone}
                      width={frameSize}
                      height={(frameSize * 461) / 228.7}
                      color={"brand.secondary"}
                    />
                    <Box
                      mt={2}
                      borderRadius={20}
                      position={"absolute"}
                      width={imageSize}
                      height={(imageSize * 1024) / 472}
                      overflow={"hidden"}
                      role={"group"}
                    >
                      <Box
                        opacity={1}
                        height={"auto"}
                        transition={"opacity 0.5s ease-in-out"}
                        _groupHover={{ opacity: 0, height: 0 }}
                        position={"relative"}
                      >
                        <Image
                          src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}${image}`}
                          loader={({ src }) => src}
                          width={472}
                          height={1024}
                          priority={false}
                          alt={name || "eMenu"}
                        />
                      </Box>
                      <Box
                        opacity={0}
                        height={0}
                        transition={"opacity 0.5s ease-in-out"}
                        _groupHover={{ opacity: 1, height: "auto" }}
                        position={"relative"}
                      >
                        <Image
                          src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}${hover_image}`}
                          loader={({ src }) => src}
                          width={472}
                          height={1024}
                          priority={false}
                          alt={name || "eMenu"}
                        />
                      </Box>
                    </Box>
                  </VStack>
                  <VStack
                    alignItems={"center"}
                    position={"relative"}
                    display={"flex"}
                    width={140}
                    height={140}
                  >
                    <Box
                      w={{ base: 20 }}
                      h={{ base: 20 }}
                      bg={color || "brand.gray"}
                      borderRadius={"full"}
                      position={"absolute"}
                      right={-4}
                      top={-4}
                      zIndex={0}
                    />
                    <Image
                      src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}${qr_code_svg}`}
                      loader={({ src }) => src}
                      width={120}
                      height={120}
                      alt={`${name} QR Code`}
                      priority={false}
                      style={{
                        zIndex: 1,
                      }}
                    />
                  </VStack>
                </VStack>
              </Link>
            )
          )}
        </Flex>
      ))}
    </Carousel>
  )
}
